import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ImageLayer from 'ol/layer/Image';
import Projection from 'ol/proj/Projection';
import Static from 'ol/source/ImageStatic';
import { Extent, getCenter } from 'ol/extent';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import Draw, { createBox, DrawEvent } from 'ol/interaction/Draw';
import GeoJSON from 'ol/format/GeoJSON';
import { Select, Translate } from 'ol/interaction';
import { Fill, Stroke, Style } from 'ol/style';
import Feature from 'ol/Feature';
import { Geometry, Polygon } from 'ol/geom';
import { MapImage, Resource } from '../../types/game-document';
import { MapIllustrationContext } from './map-illustration-context';
import { MapAction, zoneStyle } from './map-canvas';
import { click } from 'ol/events/condition';
import {
  MapEntity,
  TaskEntity,
  ZoneEntity
} from '../../types/game-document/entities';
import View from 'ol/View';
import { GameDocumentContext } from '../../contexts/game-document';
import {
  GetMapById,
  GetResourceValue,
  UpdateGameDocState,
  UpdateTaskAsync
} from '../../utils/game-document';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash.clonedeep';
import { appStore } from '../../stores/app-store';
import { TaskRoute } from '../../types/game-document/entities/routes';
import { MapImagePropertyEditorWindow } from '../../features/game-document/maps/map-image-property-editor';
import {
  MAP_OBJECT_ALL,
  MAP_OBJECT_IMAGE,
  MAP_OBJECT_ZONE
} from '../../constants/map';
import { Coordinate } from 'ol/coordinate';
import { EventsKey } from 'ol/events';
import { toLonLat } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import { Collection, Overlay } from 'ol';
import GeoImage from 'ol-ext/source/GeoImage';
import Transform from 'ol-ext/interaction/Transform';
import {
  createCustomLabelPosition,
  getTopPoint
} from '../../utils/map/label-position';
import Popup from 'ol-ext/overlay/Popup';
import { moveToTop } from '../../utils/map/task-position';
import { mapContentHighlight } from '../../utils/map/map-content-highlight';

// Map views always need a projection.  Here we just want to map image
// coordinates directly to map coordinates, so we create a projection that uses
// the image extent in pixels.
interface ImageResolution {
  height: number;
  width: number;
}

interface MapCanvasProps {
  url?: string;
  showAllZones: boolean;
  isShowZoneNameVisibility: boolean;
  onAddZone?: (zone: ZoneEntity) => void;
  zones?: ZoneEntity[];
  onAddTask?: (coordinates: [number, number]) => void;
  tasks?: TaskEntity[];
  routes?: TaskRoute[];
  selectedPanel?: MapAction;
  isClearInteractions?: boolean;
  resourceImages?: Resource[];
  onSetMapTypeZone?: (e: any) => void;
  onEditZone?: (id: string, geometry: Geometry) => void;
  onEditTasks?: (tasks: TaskEntity) => void;
  onEditMapImages?: (mapImage: MapImage) => void;
  isEditZone?: boolean;
  assetMap?: MapEntity;
  mapImages?: MapImage[];
  onRemoveSelectAllAssets?: () => void;
  selectedObjectType?: 'ALL' | 'ZONE' | 'TASK' | 'TASK' | 'IMAGE' | 'AREA' | '';
  addTaskOverlay: (
    id: string,
    name: string,
    iconUrl: string,
    position: Coordinate | number[],
    isAddTaskMode?: boolean
  ) => void;
  updateTaskOverlayPosition: (
    id: string,
    newPosition: Coordinate | number[]
  ) => void;
  removeTaskOverlay: (id: string) => void;
  imageLayersRef: React.RefObject<Record<string, ImageLayer<GeoImage>>>;
  updateOverlayImage: (
    id: string,
    newCenter: [number, number],
    newExtent: [number, number, number, number],
    newScale: [number, number] | undefined
  ) => void;
  setVectorPopupId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const addTaskMode = {
  id: '00000',
  name: '',
  image: 'https://cdn.catalystglobal.games/resources/map-task.png'
};

const geojsonObject = {
  type: 'FeatureCollection',
  crs: {
    type: 'name',
    properties: {
      name: 'EPSG:3857'
    }
  },
  features: []
};

const tempSource = new VectorSource({
  features: new GeoJSON().readFeatures(geojsonObject)
});

const getImage = (url: string): Promise<ImageResolution> => {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.src = url;
    img.onload = () => {
      const imgRes: ImageResolution = {
        width: img.width,
        height: img.height
      };
      resolve(imgRes);
    };
    img.onerror = (e) => {
      reject(e);
    };
  });
};

const MapIllustrationCanvas = ({
  url,
  zones,
  tasks,
  routes,
  assetMap,
  mapImages,
  isEditZone,
  showAllZones,
  selectedPanel,
  resourceImages,
  imageLayersRef,
  selectedObjectType,
  isClearInteractions,
  isShowZoneNameVisibility,
  onAddZone,
  onAddTask,
  onEditZone,
  onEditTasks,
  addTaskOverlay,
  setVectorPopupId,
  removeTaskOverlay,
  updateOverlayImage,
  updateTaskOverlayPosition,
  onEditMapImages = () => {},
  onRemoveSelectAllAssets = () => {}
}: MapCanvasProps) => {
  const [state, setState] = useContext(GameDocumentContext);
  let map = useContext(MapIllustrationContext);
  const mapRef = useRef<HTMLDivElement>(null);
  const [imageIllustration, setImageIllustration] = useState<ImageResolution>();
  const [imageLayer, setImageLayer] = useState<any>();
  const maps = isEditZone ? assetMap : state.gameDocument?.assets.maps![0];
  const [showImageEditor, setShowImageEditor] = useState<boolean>(false);
  const selectRef = useRef<string[]>([]);
  const [moveListenerKeys, setMoveListenerKeys] = useState<EventsKey[]>([]);
  const [clickListenerKeys, setClickListenerKeys] = useState<EventsKey[]>([]);

  const OBJECT_TYPE = 'objectType';

  const onMapMoveEnd = debounce(() => {
    setState((prev) => {
      const newGameDoc = cloneDeep(prev);
      const mapEntity = GetMapById(
        newGameDoc.gameDocument!,
        isEditZone
          ? assetMap?.id!
          : newGameDoc?.gameDocument?.rules?.worldMap?.mapAssId!
      );
      const center = map.getView().getCenter();
      if (mapEntity && center) {
        mapEntity.zoomLevel = map.getView().getZoom();
        mapEntity.longitude = center[0];
        mapEntity.latitude = center[1];
      }
      return UpdateGameDocState(prev, newGameDoc.gameDocument!);
    });
  }, 200);

  const constrainFeatureToMapBounds = (
    feature: Feature<Geometry>,
    mapExtent: Extent
  ): boolean => {
    const geometry = feature.getGeometry();
    if (!geometry) return false;

    const featureExtent = geometry.getExtent();
    let isModified = false;

    // X-axis constraints
    if (featureExtent[0] < mapExtent[0]) {
      geometry.translate(mapExtent[0] - featureExtent[0], 0);
      isModified = true;
    }
    if (featureExtent[2] > mapExtent[2]) {
      geometry.translate(mapExtent[2] - featureExtent[2], 0);
      isModified = true;
    }

    // Y-axis constraints
    if (featureExtent[1] < mapExtent[1]) {
      geometry.translate(0, mapExtent[1] - featureExtent[1]);
      isModified = true;
    }
    if (featureExtent[3] > mapExtent[3]) {
      geometry.translate(0, mapExtent[3] - featureExtent[3]);
      isModified = true;
    }

    if (isModified) {
      feature.setGeometry(geometry);
    }

    return isModified;
  };

  const selectAllObject = () => {
    const layers = map.getAllLayers();

    const select = new Select({
      condition: click,
      layers: [layers as any] // Use an array with VectorLayer
    });

    map.addInteraction(select);
    const allFeatures: Feature[] = [];

    map.getLayers().forEach((layer) => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        if (source instanceof VectorSource) {
          // Collect features from the source
          const features = source.getFeatures();

          if (selectedObjectType === MAP_OBJECT_ALL) {
            allFeatures.push(...features);
          } else {
            allFeatures.push(
              ...features.filter(
                (feature) => feature.get(OBJECT_TYPE) === selectedObjectType
              )
            );
          }

          if (selectRef.current.length && selectedObjectType === '') {
            allFeatures.push(
              ...features.filter((feature) =>
                selectRef.current.includes(feature.getId() as string)
              )
            );
          }
        }
      }
    });

    select.getFeatures().clear();
    select.getFeatures().extend(allFeatures);

    // Create a Translate interaction
    const translate = new Translate({
      features: select.getFeatures()
    });

    // Add the Translate interaction to the map
    map.addInteraction(translate);

    const startPixels = { x: 0, y: 0 };
    const overlays: Overlay[] = [];
    const updatedOverlayPositions: [number, number][] = [];
    if (selectedObjectType === 'ALL') {
      map
        .getOverlays()
        .getArray()
        .forEach((ov) => overlays.push(ov));
    }

    translate.on('translatestart', (event) => {
      const translatedFeature =
        event.features.getArray()[0] as Feature<Geometry>;
      const geometry = translatedFeature.getGeometry();
      const startFeaturePosition = map.getPixelFromCoordinate(
        (geometry as Polygon).getFirstCoordinate()
      );
      startPixels.x = startFeaturePosition[0];
      startPixels.y = startFeaturePosition[1];
    });

    translate.on('translating', (event) => {
      const translatedFeature =
        event.features.getArray()[0] as Feature<Geometry>;
      const geometry = translatedFeature.getGeometry();
      const newFeaturePosition = map.getPixelFromCoordinate(
        (geometry as Polygon).getFirstCoordinate()
      );

      const deltaX = newFeaturePosition[0] - startPixels.x;
      const deltaY = newFeaturePosition[1] - startPixels.y;

      // Apply constraints to all features
      const mapExtent = map.getView().getProjection().getExtent();
      event.features.forEach((feat) => {
        constrainFeatureToMapBounds(feat, mapExtent);
      });

      overlays.forEach((ov, idx) => {
        const currentCoordinate = ov.getPosition();

        if (currentCoordinate) {
          const currentPixel = map.getPixelFromCoordinate(currentCoordinate);
          const newPixelPosition = [
            currentPixel[0] + deltaX,
            currentPixel[1] + deltaY
          ];
          const newCoordinatePosition =
            map.getCoordinateFromPixel(newPixelPosition);

          // Constrain overlay positions to map boundaries
          if (newCoordinatePosition[0] < mapExtent[0])
            newCoordinatePosition[0] = mapExtent[0];
          if (newCoordinatePosition[0] > mapExtent[2])
            newCoordinatePosition[0] = mapExtent[2];
          if (newCoordinatePosition[1] < mapExtent[1])
            newCoordinatePosition[1] = mapExtent[1];
          if (newCoordinatePosition[1] > mapExtent[3])
            newCoordinatePosition[1] = mapExtent[3];

          ov.setPosition(newCoordinatePosition);
          updatedOverlayPositions[idx] = [
            newCoordinatePosition[0],
            newCoordinatePosition[1]
          ];
        }
      });

      startPixels.x = newFeaturePosition[0];
      startPixels.y = newFeaturePosition[1];
    });

    // Handle the "translateend" event
    translate.on('translateend', (evt) => {
      const mapExtent = map.getView().getProjection().getExtent();

      evt.features.forEach((feat) => {
        let objectType = feat.get(OBJECT_TYPE);
        let lastPoint = feat.getGeometry();
        let featureId = feat.getId()?.toString() ?? '';
        constrainFeatureToMapBounds(feat, mapExtent);

        if (objectType === MAP_OBJECT_ZONE) {
          updateFeatureZone(
            featureId.replaceAll('zone-', ''),
            feat.getGeometry() as Geometry
          );
        } else if (objectType === MAP_OBJECT_IMAGE) {
          const newExtent = lastPoint?.getExtent() as [
            number,
            number,
            number,
            number
          ];
          const newCenter = getCenter(newExtent) as [number, number];
          updateOverlayImage(featureId, newCenter, newExtent, undefined);
        }
      });

      if (tasks && updatedOverlayPositions.length > 0) {
        overlays.forEach((ov, idx) => {
          const taskId = ov.getId();
          if (taskId) {
            const selectedTask = tasks.find((task) => task.id === taskId);
            if (state.gameDocument && selectedTask && selectedTask.boundary) {
              selectedTask.boundary.geometry.coordinates =
                updatedOverlayPositions[idx];

              UpdateTaskAsync(
                state.gameDocument,
                taskId as string,
                selectedTask
              ).then((response) => {
                setState((prev) => UpdateGameDocState(prev, response));
              });
            }
          }
        });
      }

      // Remove the select and translate interactions when done
      map.removeInteraction(select);
      map.removeInteraction(translate);
    });
  };

  useEffect(() => {
    if (selectedObjectType) {
      selectAllObject();
    } else {
      map
        .getInteractions()
        .getArray()
        .filter(
          (interaction) =>
            interaction instanceof Select || interaction instanceof Translate
        )
        .forEach((interaction) => map.removeInteraction(interaction));
      onRemoveSelectAllAssets();
    }
  }, [map, selectedObjectType]);

  useEffect(() => {
    if (url) {
      appStore.showLoading();
      getImage(url)
        .then((res) => setImageIllustration(res))
        .finally(() => {
          appStore.hideLoading();
        });
    }
  }, [url]);

  useEffect(() => {
    if (imageIllustration) {
      //#region create map Image Layer
      map.setTarget(mapRef.current as HTMLElement);

      let extent: Extent;
      extent = [0, 0, imageIllustration.width, imageIllustration.height];
      const projection = new Projection({
        code: 'xkcd-image',
        units: 'pixels',
        extent: extent
      });

      map.setView(
        new View({
          projection: projection,
          center:
            maps!.latitude !== undefined && maps!.longitude !== undefined
              ? [maps!.longitude, maps!.latitude]
              : getCenter(extent),
          extent,
          zoom: 1
        })
      );

      if (maps!.zoomLevel !== undefined) {
        map.getView().setZoom(maps?.zoomLevel!);
      }

      const imageLayer = new ImageLayer({
        source: new Static({
          attributions: '',
          url: url ?? '',
          projection: projection,
          imageExtent: extent
        })
      });

      setImageLayer(imageLayer);
      const layers = map.getAllLayers();
      if (layers.length > 0) {
        layers[0] = imageLayer;
        map.setLayers(layers);
      } else {
        map.setLayers([imageLayer]);
      }

      //#endregion

      return () => {
        map.getOverlays().clear();
        map.getLayers().clear();
        map.setTarget(undefined);
      };
    }
  }, [imageIllustration, map, isEditZone, url]);

  const zoneAreaSourceRef = useRef<VectorSource | null>(null);
  const transformRef = useRef<Transform | null>(null);
  const imageTransformRef = useRef<Transform | null>(null);

  useEffect(() => {
    if (!map || !imageLayer) return;

    const mapImageLayer = new VectorLayer({
      source: new VectorSource(),
      properties: { id: 'vectorImage' },
      style: new Style({
        fill: new Fill({
          color: 'rgba(0,0,0,0)'
        })
      })
    });
    map.addLayer(mapImageLayer);

    const routeLayer = new VectorLayer({
      source: new VectorSource({ wrapX: false }),
      properties: { id: 'vectorRoute' },
      style: [
        new Style({
          stroke: new Stroke({
            color: '#408CBB',
            width: 5,
            lineCap: 'butt'
          })
        })
      ]
    });
    map.addLayer(routeLayer);

    const vectorSelectorLayer = new VectorLayer({
      source: new VectorSource(),
      style: new Style({
        fill: new Fill({
          color: 'rgba(0,0,0,0)'
        })
      })
    });
    vectorLayerRef.current = vectorSelectorLayer;

    const imageTransform = new Transform({
      translate: true,
      scale: true,
      rotate: false,
      stretch: false,
      layers: [vectorSelectorLayer],
      keepAspectRatio: () => true
    });

    imageTransform.on(['translateend', 'scaleend'], (evt: any) => {
      const mapExtent = map.getView().getProjection().getExtent();
      const feature = evt.features.item(0);

      if (feature) constrainFeatureToMapBounds(feature, mapExtent);
      if (transformEventRef.current) transformEventRef.current(evt);
    });

    imageTransform.on('select', (evt) => {
      const features = evt.features || [evt.feature];

      features.forEach((feature) => {
        const featureId = feature.getId()?.toString() ?? '';

        mapContentHighlight('zone', featureId.replace(`zone-`, ''));
      });
    });

    imageTransformRef.current = imageTransform;
    map.set('transformInteractionImage', imageTransform);
    map.addInteraction(imageTransform);

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();

      const pixel = map.getEventPixel(e);
      const feature = map.forEachFeatureAtPixel(pixel, (f) => f, {
        layerFilter: (layer) =>
          ['vectorImage', 'vectorZoneArea'].includes(layer.get('id'))
      });
      if (!feature) return;

      const featureId = feature.getId()?.toString() ?? '';
      if (!featureId) return;

      const coordinate = map.getCoordinateFromPixel(pixel);
      const popupOverlay = map.getOverlayById('vector-menu-popup') as Popup;
      if (!popupOverlay) return;

      popupOverlay.show(
        coordinate,
        `<div id="vector-menu-popup-${featureId}"></div>`
      );
      setVectorPopupId(featureId);

      const popup = document.querySelector(
        '.ol-popup:has([id^="vector-menu"])'
      );
      if (popup) {
        const button = popup.querySelector('button.closeBox');
        if (button) button.remove();

        const container = document.querySelector(
          '.ol-overlaycontainer-stopevent'
        );
        if (container) moveToTop(container, popup, 3);
      }
    };

    mapRef.current?.addEventListener('contextmenu', handleContextMenu);

    const layers = map.getAllLayers();
    const isZoneAreaLayerExist = layers.find(
      (l) => l.get('id') === 'vectorZoneArea'
    );
    if (isZoneAreaLayerExist || isEditZone) return;

    const zoneAreaSource = new VectorSource();
    zoneAreaSourceRef.current = zoneAreaSource;

    const zoneAreaLayer = new VectorLayer({
      properties: {
        id: 'vectorZoneArea'
      },
      source: zoneAreaSource
    });
    map.addLayer(zoneAreaLayer);

    zoneAreaSource.on('removefeature', (evt) => {
      const removedFeature = evt.feature;
      transform.select(removedFeature, false);
    });

    const transform = new Transform({
      translate: true,
      scale: true,
      rotate: true,
      layers: [zoneAreaLayer],
      keepAspectRatio: () => true
    });

    transform.on('select', (evt) => {
      const features = evt.features || [evt.feature];

      features.forEach((feature) => {
        const featureId = feature.getId()?.toString() ?? '';
        mapContentHighlight('image', featureId.replace(`image-`, ''));
      });
    });

    transform.on(['rotateend', 'scaleend', 'translateend'], (evt: any) => {
      const features = evt.features || [evt.feature];

      features.forEach((feature: Feature) => {
        const geometry = feature.getGeometry();
        if (!geometry) return;

        const featureId = feature.getId()?.toString() ?? '';
        updateFeatureZone(featureId.replaceAll('zone-', ''), geometry);
      });
    });

    transform.on(['rotatestart', 'scalestart', 'translatestart'], () => {
      imageTransform.setSelection(new Collection<Feature<Geometry>>([]));
    });

    const postrenderKey = map.on('postrender', () => {
      // Get all features
      const features = zoneAreaSource.getFeatures();

      // Update each label position based on its corresponding area/zone
      features.forEach((feature) => {
        const id = feature.getId()?.toString() ?? '';

        const label = map.getOverlayById(id);
        if (label && feature.getGeometry()) {
          const geometry = feature.getGeometry() as Polygon;
          if (geometry)
            label.setPosition(getTopPoint(geometry.getCoordinates()[0] as any));
        }
      });
    });

    transformRef.current = transform;
    map.set('transformInteraction', transform);
    map.addInteraction(transform);

    return () => {
      mapRef.current?.removeEventListener('contextmenu', handleContextMenu);
      map.removeInteraction(transform);
      map.removeInteraction(imageTransform);
      map.removeLayer(zoneAreaLayer);
      map.removeLayer(mapImageLayer);
      map.removeLayer(vectorSelectorLayer);
      unByKey(postrenderKey);
    };
  }, [map, isEditZone, imageLayer]);

  useEffect(() => {
    const zoneSource = zoneAreaSourceRef.current;
    const transform = transformRef.current;
    if (!zoneSource || !zones || isEditZone || !imageLayer) return;

    zones.forEach((worldZone) => {
      let id = worldZone.boundary?.id;
      if (!id) return;
      id = `zone-${id}`;

      let zoneFeature = zoneSource.getFeatureById(id) as Feature<Geometry>;

      if (!zoneFeature) {
        zoneFeature = new GeoJSON().readFeature(
          worldZone.boundary as never
        ) as Feature<Geometry>;
        zoneFeature.setId(id);
        zoneFeature.set(OBJECT_TYPE, MAP_OBJECT_ZONE);
        zoneSource.addFeature(zoneFeature);
      } else {
        const updatedGeometry = new GeoJSON().readFeature(
          worldZone.boundary as never
        ) as Feature<Geometry>;
        zoneFeature.setGeometry(updatedGeometry.getGeometry());
      }

      zoneFeature.setStyle(
        zoneStyle(showAllZones ? true : worldZone.isVisible, 'Zone')
      );

      if (!worldZone.isVisible) transform?.select(zoneFeature, false);
      zoneFeature.changed();

      const labelOverlay = map.getOverlayById(id);

      if (showAllZones || (worldZone.isVisible && zoneFeature.getGeometry())) {
        if (!labelOverlay) {
          const title = GetResourceValue(
            state.gameDocument!,
            worldZone.titleResId ?? ''
          );

          const labelElement = createCustomLabelPosition(id, title);

          const overlay = new Overlay({
            id,
            position: getTopPoint(
              (zoneFeature.getGeometry() as Polygon).getCoordinates()[0] as any
            ),
            positioning: 'center-center',
            element: labelElement,
            stopEvent: false
          });

          map.addOverlay(overlay);
        }
      } else {
        if (labelOverlay) map.removeOverlay(labelOverlay);
      }
    });

    if (selectedObjectType) selectAllObject();
  }, [
    zones,
    isEditZone,
    imageLayer,
    showAllZones,
    state.gameDocument,
    selectedObjectType,
    isShowZoneNameVisibility
  ]);

  useEffect(() => {
    let drawZone: any;
    let drawArea: any;

    const drawZoneHandler = (event: DrawEvent) => {
      if (!onAddZone) return;
      if (!event || !event.feature) return;
      const data = event.feature.getGeometry() as Polygon;
      const coordinates = data.getCoordinates();

      onAddZone({
        id: '',
        name: '',
        description: '',
        boundary: {
          id: '',
          type: 'Polygon',
          geometry: coordinates as any
        }
      });
    };

    if (selectedPanel === 'Zone') {
      drawZone = new Draw({
        source: tempSource,
        type: 'Circle',
        geometryFunction: createBox()
      });

      drawZone.on('drawend', drawZoneHandler);
      map.addInteraction(drawZone);
    }

    return () => {
      if (selectedPanel === 'Zone') {
        drawZone.un('drawend', drawZoneHandler);
        map.removeInteraction(drawZone);
      }
    };
  }, [selectedPanel]);

  const updateFeatureZone = (id: string, geometry: Geometry) => {
    onEditZone && onEditZone(id, geometry);
  };

  useEffect(() => {
    map.on('moveend', onMapMoveEnd);

    map.on('click', function (event) {
      selectRef.current = [];
      onRemoveSelectAllAssets();
    });

    return () => {
      map.un('moveend', onMapMoveEnd);
    };
  }, [map, onMapMoveEnd]);

  const [moveListener, setMoveListener] = useState<EventsKey | null>(null);
  const [clickListener, setClickListener] = useState<EventsKey | null>(null);

  useEffect(() => {
    if (selectedPanel === 'Task' && map) {
      const { id, image, name } = addTaskMode;
      let defaultImage = image;

      if (
        state.gameDocument?.settings.designer &&
        state.gameDocument.settings.designer.defaultTaskAvailableIconResId
      ) {
        defaultImage = GetResourceValue(
          state.gameDocument,
          state.gameDocument.settings.designer.defaultTaskAvailableIconResId
        );
      }

      addTaskOverlay(id, name, defaultImage, [0, 0], true);

      const moveListenerKey = map.on('pointermove', (event) => {
        const coordinate = event.coordinate;
        updateTaskOverlayPosition(id, toLonLat(coordinate));
      });

      const clickListenerKey = map.on('click', (event) => {
        const coordinate = event.coordinate;
        onAddTask && onAddTask([coordinate[0], coordinate[1]]);
      });

      setMoveListener(moveListenerKey);
      setClickListener(clickListenerKey);

      moveListenerKeys.push(moveListenerKey);
      clickListenerKeys.push(clickListenerKey);
    }
  }, [selectedPanel, map, state.gameDocument]);

  useEffect(() => {
    if (selectedPanel !== 'Task' && map) {
      if (moveListener) unByKey(moveListener);
      if (clickListener) unByKey(clickListener);
      removeTaskOverlay(addTaskMode.id);

      if (moveListenerKeys.length > 0) {
        moveListenerKeys.forEach((moveListenerKey) => {
          unByKey(moveListenerKey);
        });
      }

      if (clickListenerKeys.length > 0) {
        clickListenerKeys.forEach((clickListenerKey) => {
          unByKey(clickListenerKey);
        });
      }
    }
  }, [selectedPanel, map, moveListener, clickListener]);

  const [imageDimentions, setImageDimentions] =
    useState<Record<string, { width: number; height: number }>>();

  const vectorLayerRef = useRef<VectorLayer | null>(null);
  const transformEventRef = useRef<((evt: any) => void) | null>(null);

  const [selectedImageFeature, setSelectedImageFeature] =
    useState<Feature | null>(null);

  useEffect(() => {
    if (!transformRef || !transformRef.current) return;

    // set transform image overlay as inactive if on add Area, Zone, Task, Image, or Annotation
    transformRef.current.setActive(selectedPanel === '');
    if (selectedPanel) setShowImageEditor(false);
  }, [transformRef, selectedPanel]);

  const imageList = useMemo(() => {
    const result: {
      id: string;
      url: string;
      opacity: number;
      center: [number, number];
      scale: [number, number];
      isVisible: boolean;
      isLocked: boolean;
    }[] = [];

    if (mapImages && mapImages?.length > 0) {
      mapImages.forEach((image) => {
        if (image.imageResId) {
          const url = GetResourceValue(state.gameDocument!, image.imageResId);
          result.push({
            id: `image-${image.id}`,
            url,
            opacity: image.opacity ?? 1,
            center: image.center ?? [0, 0],
            scale: image.scale ?? [1, 1],
            isVisible: image.isVisible ?? true,
            isLocked: image.isLocked ?? false
          });
        }
      });
    }

    return result;
  }, [mapImages, state.gameDocument]);

  const transformEvent = useCallback(
    (evt: any) => {
      if (!imageDimentions) return;

      const feature: Feature = evt.feature;
      const extent = feature.getGeometry()?.getExtent();
      const id = feature.getId() as string;
      const dimention = imageDimentions[id];

      if (!extent || !dimention) return;

      const newCenter = getCenter(extent!) as [number, number];

      const originalWidth = dimention.width;
      const originalHeight = dimention.height;
      const newWidth = extent[2] - extent[0];
      const newHeight = extent[3] - extent[1];
      const scaleX = (newWidth / originalWidth) * 100;
      const scaleY = (newHeight / originalHeight) * 100;

      const vectorImageTool = map
        .getAllLayers()
        .find((layer) => layer.get('id') === 'vectorImage');

      if (vectorImageTool) {
        const source = vectorImageTool.getSource() as VectorSource;
        const vectorFeature = source?.getFeatureById(id) as Feature<Polygon>;
        if (vectorFeature) {
          const geometry = feature.getGeometry();
          if (geometry && geometry.getType() === 'Polygon') {
            vectorFeature.setGeometry(geometry.clone() as Polygon);
          }
        }
      }

      updateOverlayImage(
        id,
        newCenter,
        extent as [number, number, number, number],
        [scaleX, scaleY]
      );
    },
    [state.gameDocument, imageDimentions, updateOverlayImage]
  );

  useEffect(() => {
    transformEventRef.current = transformEvent;
  }, [transformEvent]);

  useEffect(() => {
    if (!map || !imageLayer) return;

    const layers = map.getAllLayers();
    const existingImageLayers = layers.filter((layer) =>
      layer.get('id')?.startsWith('image-')
    );
    existingImageLayers.forEach((layer) => map.removeLayer(layer));
    imageLayersRef.current = {};

    if (!imageList || imageList.length === 0) return;

    const imageLayers: ImageLayer<GeoImage>[] = [];

    // wait for all images to load before adding layers
    const promises = imageList.map(
      (imgData) =>
        new Promise<void>((resolve) => {
          const { id, url, center, scale, opacity = 1, isVisible } = imgData;

          const image = new Image();
          image.src = url;
          image.onload = () => {
            setImageDimentions((prev) => ({
              ...prev,
              [id]: { width: image.width * 100, height: image.height * 100 }
            }));

            const source = new GeoImage({
              url,
              imageCenter: center,
              imageScale: scale
            });

            const layer = new ImageLayer({
              source,
              properties: { id },
              opacity,
              visible: isVisible
            });

            imageLayers.push(layer);
            imageLayersRef.current[id] = layer;

            resolve();
          };

          image.onerror = () => {
            console.warn(`Failed to load image: ${url}`);
            resolve();
          };
        })
    );
    // once all images are loaded, update the layer order
    Promise.all(promises).then(() => {
      if (imageLayers.length > 0) {
        const illustrationLayer = layers[0];
        const otherLayers = layers
          .slice(1)
          .filter((layer) => !layer.get('id')?.startsWith('image-'));

        imageLayers.forEach((layer) => {
          const source = layer.getSource();
          const id = layer.get('id');
          if (!source || !id) return;

          const imageExtent = source.getExtent();
          const imageFeature = new Feature({
            geometry: new Polygon([
              [
                [imageExtent[0], imageExtent[1]],
                [imageExtent[0], imageExtent[3]],
                [imageExtent[2], imageExtent[3]],
                [imageExtent[2], imageExtent[1]],
                [imageExtent[0], imageExtent[1]]
              ]
            ])
          });

          imageFeature.set(OBJECT_TYPE, MAP_OBJECT_IMAGE);
          imageFeature.setId(id);
          const imageLayer = map
            .getAllLayers()
            .find((l) => l.get('id') === 'vectorImage');

          if (imageLayer) {
            (imageLayer.getSource() as VectorSource).addFeature(imageFeature);
          }
        });

        // new order: tile → images → others
        map.setLayers([illustrationLayer, ...imageLayers, ...otherLayers]);
      }
    });

    return () => {
      imageLayers.forEach((layer) => map.removeLayer(layer));
      imageLayersRef.current = {};
    };
  }, [
    map,
    imageLayer,
    imageList.map((i) => i.id + i.url + i.opacity + i.center + i.scale).join('')
  ]);

  const imageListRef = useRef(imageList);

  useEffect(() => {
    imageListRef.current = imageList;
  }, [imageList]);

  useEffect(() => {
    if (!map || !imageLayer) return;

    const transform = imageTransformRef.current;
    const vectorLayer = vectorLayerRef.current;
    if (!map || !transform || !vectorLayer) return;

    const handleClick = (evt: any) => {
      let hitZoneOrArea = false;

      map.forEachFeatureAtPixel(
        evt.pixel,
        (feature, layer) => {
          const layerId = layer?.get('id');

          if (layerId === 'vectorZoneArea') {
            hitZoneOrArea = true;

            transformRef.current?.setSelection(
              new Collection<Feature<Geometry>>([feature as Feature])
            );
            transform.setSelection(new Collection<Feature<Geometry>>([]));
            setSelectedImageFeature(null);
            setShowImageEditor(false);

            return true;
          }

          return false;
        },
        {
          layerFilter: (layer) => layer !== vectorLayerRef.current
        }
      );

      if (hitZoneOrArea) return;

      let clickedImageId: string | null = null;

      Object.entries(imageLayersRef.current).forEach(([id, layer]) => {
        const source = layer.getSource() as GeoImage;
        const extent = source.getExtent();
        if (
          extent &&
          evt.coordinate[0] >= extent[0] &&
          evt.coordinate[0] <= extent[2] &&
          evt.coordinate[1] >= extent[1] &&
          evt.coordinate[1] <= extent[3]
        ) {
          clickedImageId = id;
        }
      });

      // Remove previous transform
      vectorLayer.getSource()?.clear();
      map.removeLayer(vectorLayer);

      if (clickedImageId) {
        const selectedImage = imageListRef.current.find(
          (img) => img.id === clickedImageId
        );

        if (
          !selectedImage ||
          selectedImage.isLocked ||
          selectedImage.isVisible === false
        )
          return;

        const layer = imageLayersRef.current[clickedImageId];
        const source = layer.getSource() as GeoImage;
        const extent = source.getExtent();
        if (!extent) return;

        const coords = [
          [
            [extent[0], extent[1]],
            [extent[0], extent[3]],
            [extent[2], extent[3]],
            [extent[2], extent[1]],
            [extent[0], extent[1]]
          ]
        ];

        // Create polygon feature for extent
        const feature = new Feature({
          geometry: new Polygon(coords)
        });
        feature.setId(clickedImageId);
        vectorLayer.getSource()?.addFeature(feature);
        map.setLayers([...map.getAllLayers(), vectorLayer]);
        setShowImageEditor(true);
        setSelectedImageFeature(feature);
        transform.setSelection(new Collection<Feature<Geometry>>([feature]));
      } else {
        setShowImageEditor(false);
        map.removeLayer(vectorLayer);
        setSelectedImageFeature(null);
      }
    };

    map.on('click', handleClick);
    return () => {
      map.un('click', handleClick);
      setSelectedImageFeature(null);
    };
  }, [map, imageLayer]);

  useEffect(() => {
    if (!selectedImageFeature) return;

    const featureId = selectedImageFeature.getId();
    if (!featureId) return;

    const selectedImage = imageList.find((img) => img.id === featureId);

    if (!selectedImage || (selectedImage && selectedImage.isLocked)) {
      const transform = imageTransformRef.current;
      const vectorLayer = vectorLayerRef.current;

      if (!map || !transform || !vectorLayer) return;

      transform.select(selectedImageFeature, false);
      vectorLayer.getSource()?.clear();
      map.removeLayer(vectorLayer);
      setSelectedImageFeature(null);
      setShowImageEditor(false);
    }
  }, [imageList, selectedImageFeature]);

  return (
    <div className={'position-relative h-full w-full'}>
      <div id={'map'} ref={mapRef as any} className={'map w-full h-full'} />
      {showImageEditor && (
        <MapImagePropertyEditorWindow
          key={selectedImageFeature?.getId() as string}
          id={(selectedImageFeature?.getId() as string) ?? ''}
          onClose={() => setShowImageEditor(false)}
          imageLayersRef={imageLayersRef}
        />
      )}
    </div>
  );
};

export default MapIllustrationCanvas;
